/**
 * EPIC 13 · the round, as a set of operational registers.
 *
 * These are descriptors rather than bespoke screens because every other operational register in this ERP is
 * one: the same dense table, the same filter bar, the same column chooser, the same detail drawer with
 * evidence, links and an audit timeline. A hand-written delivery screen would drift from all of them within
 * a month.
 *
 * WHAT THESE SCREENS DELIBERATELY DO NOT SHOW
 *
 *   No money. Not a price, not a deposit, not a charge on a returned bottle, not a rider's earnings. The
 *   source stripped rates from every rider-facing sheet and the incentive decisions are all unanswered.
 *
 *   No invented certainty. A door with no GPS pin reads "No pin" in its own column rather than showing a
 *   sequence number that implies a confidence nobody has. A delivery nobody recorded reads "Pending", never
 *   zero. A counted container return says it was counted, not scanned.
 */
import {
  AlertTriangle, Ban, CalendarClock, CheckCircle2, ListChecks, MapPin, PackageCheck, PencilLine, Play, QrCode,
  Shuffle, Truck, Undo2,
} from 'lucide-react';
import { Status, StatusBadge } from '../../ui/primitives';
import { asIdList, asInteger, asScan, asText } from '../operations/ActionDialog';
import { when, type OpsDescriptor, type OpsRow } from '../operations/types';

/** A count that must not read as zero when it is actually unknown. */
const count = (value: unknown): string | null => (value === null || value === undefined ? null : String(value));

/** Units delivered against units ordered, keeping "not recorded" distinct from "none". */
function handedOver(row: OpsRow) {
  const ordered = row.ordered_units as number | null;
  const delivered = row.delivered_units as number | null;
  if (delivered === null || delivered === undefined) {
    return <span className="text-muted">— of {count(ordered)}</span>;
  }
  return (
    <span className={delivered < (ordered ?? 0) ? 'text-queued' : undefined}>
      {delivered} of {count(ordered)}
    </span>
  );
}

const STATUS_CELL = (key: string) => (row: OpsRow) => {
  const value = row[key];
  return value ? <Status code={String(value)} /> : null;
};

export const DELIVERY: Record<string, OpsDescriptor> = {
  // ── Delivery planning · the day's intent, per route ────────────────────────────────────────────
  'delivery-plans': {
    id: 'delivery-plans', title: 'Delivery Planning', captures: false,
    subtitle:
      'The day’s intent for one route, built from CONFIRMED orders only. A plan is not a promise and not a delivery: it moves no stock, and every line traces to the order it came from.',
    footer: 'Doors with no GPS pin are ordered by the legacy round hint and counted separately — they are not given a coordinate',
    viewPermission: 'delivery.view', view: 'delivery_plans',
    select: 'id,code,service_date,status,origin,route_code,route_name,line_count,door_count,doors_without_a_pin,units_planned,trip_count,note,confirmed_at,cancel_reason,created_at,updated_at,row_version',
    searchColumns: ['code', 'route_code'], defaultSort: 'service_date', defaultSortDirection: 'desc',
    filters: [
      { key: 'service_date', kind: 'dateRange', label: 'Service date' },
      { key: 'status', kind: 'select', label: 'Status', options: [
        { value: 'DRAFT', label: 'Draft' }, { value: 'CONFIRMED', label: 'Confirmed' }, { value: 'CANCELLED', label: 'Cancelled' }] },
      { key: 'route_code', kind: 'search', label: 'Route', placeholder: 'Route code' },
    ],
    columns: [
      { key: 'code', header: 'Plan', width: '10rem', mono: true },
      { key: 'service_date', header: 'Service date', width: '9rem' },
      { key: 'status', header: 'Status', width: '8rem', render: STATUS_CELL('status') },
      { key: 'route_code', header: 'Route', width: '7rem', mono: true },
      { key: 'door_count', header: 'Doors', width: '6rem', numeric: true },
      { key: 'doors_without_a_pin', header: 'No pin', width: '6rem', numeric: true,
        render: (r) => (Number(r.doors_without_a_pin) > 0
          ? <StatusBadge tone="queued" icon={MapPin} title="Ordered by the legacy round hint, because these doors have no GPS pin">{String(r.doors_without_a_pin)}</StatusBadge>
          : <span className="text-muted">0</span>) },
      { key: 'line_count', header: 'Lines', width: '5rem', numeric: true },
      { key: 'units_planned', header: 'Units', width: '6rem', numeric: true },
      { key: 'trip_count', header: 'Rounds', width: '6rem', numeric: true },
      { key: 'note', header: 'Note', optional: true },
    ],
    actions: [
      {
        key: 'build', label: 'Build the day’s plan', icon: CalendarClock, permission: 'delivery.plan.manage',
        rpc: 'build_delivery_plan', scope: 'global',
        intro: <>Takes every <strong>CONFIRMED</strong> order for that route and date. Planned and cancelled orders are left out, and nothing moves in stock.</>,
        fields: [
          { key: 'service_date', label: 'Service date', kind: 'date', required: true },
          { key: 'route_id', label: 'Route', kind: 'ref', required: true, view: 'routes', labelColumns: ['code', 'name'] },
          { key: 'reason', label: 'Note', kind: 'text' },
        ],
        params: (v, _r, key) => ({ p_service_date: asText(v.service_date), p_route_id: asText(v.route_id), p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Planned.'),
        invalidates: ['delivery-plans', 'trips'],
      },
      {
        key: 'confirm', label: 'Confirm', icon: CheckCircle2, permission: 'delivery.plan.manage',
        rpc: 'set_delivery_plan_status', scope: 'row',
        available: (r) => r.status === 'DRAFT',
        intro: <>A round can only be run against a confirmed plan.</>,
        fields: [{ key: 'reason', label: 'Note', kind: 'text' }],
        params: (v, r, key) => ({ p_plan_id: r?.id, p_status: 'CONFIRMED', p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Confirmed.'),
        invalidates: ['delivery-plans'],
      },
      {
        key: 'cancel', label: 'Cancel', icon: Ban, permission: 'delivery.plan.manage',
        rpc: 'set_delivery_plan_status', scope: 'row',
        available: (r) => r.status !== 'CANCELLED',
        intro: <>The orders behind the plan are untouched. A round already under way must be cancelled first.</>,
        fields: [{ key: 'reason', label: 'Why', kind: 'textarea', required: true }],
        params: (v, r, key) => ({ p_plan_id: r?.id, p_status: 'CANCELLED', p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Cancelled.'),
        invalidates: ['delivery-plans'],
      },
    ],
    detail: { entityType: 'delivery.delivery_plans', title: (r) => `Plan · ${String(r.code)}`, evidence: false },
  },

  // ── Trips · one rider running one route on one date ────────────────────────────────────────────
  trips: {
    id: 'trips', title: 'Trips', captures: true,
    subtitle:
      'One rider’s run of one route on one date. A plan may be run twice; a trip is one attempt. The unrecorded column is the one that matters: hand-overs nobody ever entered.',
    footer: 'Assigning a round implies no pay, incentive or allowance — what a round is worth to a rider is unanswered',
    viewPermission: 'logistics.trips.view', view: 'trips',
    select: 'id,code,service_date,status,route_code,route_name,rider_name,vehicle_code,stop_count,stops_done,delivery_count,delivered,partial,failed,never_recorded,open_exceptions,assigned_at,loaded_at,departed_at,returned_at,closed_at,cancel_reason,created_at,updated_at,row_version',
    searchColumns: ['code', 'route_code', 'rider_name'], defaultSort: 'service_date', defaultSortDirection: 'desc',
    filters: [
      { key: 'service_date', kind: 'dateRange', label: 'Service date' },
      { key: 'status', kind: 'select', label: 'Status', options: [
        { value: 'PLANNED', label: 'Planned' }, { value: 'ASSIGNED', label: 'Assigned' }, { value: 'LOADED', label: 'Loaded' },
        { value: 'DEPARTED', label: 'On the road' }, { value: 'RETURNED', label: 'Back' },
        { value: 'RECONCILIATION_REQUIRED', label: 'To reconcile' }, { value: 'CLOSED', label: 'Closed' },
        { value: 'CANCELLED', label: 'Cancelled' }] },
      { key: 'route_code', kind: 'search', label: 'Route', placeholder: 'Route code' },
    ],
    columns: [
      { key: 'code', header: 'Round', width: '10rem', mono: true },
      { key: 'service_date', header: 'Date', width: '9rem' },
      { key: 'status', header: 'Status', width: '10rem', render: STATUS_CELL('status') },
      { key: 'route_code', header: 'Route', width: '7rem', mono: true },
      { key: 'rider_name', header: 'Rider', width: '12rem', render: (r) => (r.rider_name ? String(r.rider_name) : <span className="text-muted">Not staffed</span>) },
      { key: 'stops_done', header: 'Stops', width: '7rem', numeric: true, render: (r) => `${count(r.stops_done)} / ${count(r.stop_count)}` },
      { key: 'delivered', header: 'Delivered', width: '7rem', numeric: true },
      { key: 'partial', header: 'Part', width: '5rem', numeric: true },
      { key: 'failed', header: 'Failed', width: '6rem', numeric: true },
      { key: 'never_recorded', header: 'Unrecorded', width: '8rem', numeric: true,
        render: (r) => (Number(r.never_recorded) > 0
          ? <StatusBadge tone="queued" icon={AlertTriangle} title="Hand-overs nobody ever recorded. They stay pending: the ERP will not decide for the rider">{String(r.never_recorded)}</StatusBadge>
          : <span className="text-muted">0</span>) },
      { key: 'open_exceptions', header: 'Exceptions', width: '7rem', numeric: true, optional: true },
      { key: 'vehicle_code', header: 'Vehicle', width: '8rem', mono: true, optional: true },
      { key: 'departed_at', header: 'Left', width: '11rem', optional: true, render: (r) => when(r.departed_at) },
      { key: 'returned_at', header: 'Back', width: '11rem', optional: true, render: (r) => when(r.returned_at) },
    ],
    actions: [
      {
        key: 'create', label: 'Start a round', icon: Truck, permission: 'logistics.trips.manage',
        rpc: 'create_trip', scope: 'global',
        intro: <>Creates the stops in the plan’s order and one <strong>pending</strong> hand-over per line. Nothing is delivered and no stock moves.</>,
        fields: [
          { key: 'plan_id', label: 'Confirmed plan', kind: 'ref', required: true, view: 'delivery_plans', labelColumns: ['code', 'service_date'], where: { status: 'eq.CONFIRMED' } },
          { key: 'rider_person_id', label: 'Rider', kind: 'ref', view: 'persons', labelColumns: ['display_name'] },
          { key: 'vehicle_id', label: 'Vehicle', kind: 'ref', view: 'vehicles', labelColumns: ['code', 'name'] },
          { key: 'reason', label: 'Note', kind: 'text' },
        ],
        params: (v, _r, key) => ({ p_plan_id: asText(v.plan_id), p_rider_person_id: asText(v.rider_person_id), p_vehicle_id: asText(v.vehicle_id), p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Round created.'),
        invalidates: ['trips', 'trip-stops', 'deliveries'],
      },
      {
        key: 'load', label: 'Load the vehicle', icon: PackageCheck, permission: 'delivery.record',
        rpc: 'dispatch_to_trip', scope: 'row',
        available: (r) => r.status === 'ASSIGNED' || r.status === 'LOADED',
        intro: <>The first stock movement of the day. Name the <strong>lots</strong> being loaded — a recall has to be able to reach the customers who received a given filling lot, so no lot is chosen for you.</>,
        fields: [
          { key: 'product_variant_id', label: 'Variant', kind: 'ref', required: true, view: 'product_variants', labelColumns: ['code', 'name'] },
          { key: 'units', label: 'Units', kind: 'integer', required: true },
          { key: 'from_location_id', label: 'Loaded from', kind: 'ref', required: true, view: 'locations', labelColumns: ['code', 'name'] },
          { key: 'lots', label: 'Lots', kind: 'lines', required: true, hint: 'One per line: lot id, units' },
          { key: 'reason', label: 'Note', kind: 'text' },
        ],
        params: (v, r, key) => ({
          p_trip_id: r?.id, p_product_variant_id: asText(v.product_variant_id), p_units: asInteger(v.units),
          p_from_location_id: asText(v.from_location_id),
          p_lots: String(v.lots ?? '').split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
            const [lotId, units] = line.split(',').map((part) => part.trim());
            return { lot_id: lotId, quantity_units: Number(units) };
          }),
          p_reason: asText(v.reason), p_idempotency_key: key,
        }),
        success: (r) => String(r.detail ?? 'Loaded.'),
        invalidates: ['trips', 'stock'],
      },
      {
        key: 'advance', label: 'Move the round on', icon: Play, permission: 'logistics.trips.manage',
        rpc: 'set_trip_status', scope: 'row',
        available: (r) => !['CLOSED', 'CANCELLED'].includes(String(r.status)),
        intro: <>Planned → assigned → loaded → departed → back → closed, with reconciliation between the last two where the round did not add up.</>,
        fields: [
          { key: 'status', label: 'To', kind: 'select', required: true, options: [
            { value: 'ASSIGNED', label: 'Assigned' }, { value: 'LOADED', label: 'Loaded' }, { value: 'DEPARTED', label: 'On the road' },
            { value: 'RETURNED', label: 'Back' }, { value: 'RECONCILIATION_REQUIRED', label: 'Hold for reconciliation' },
            { value: 'CLOSED', label: 'Closed' }, { value: 'CANCELLED', label: 'Cancelled' }] },
          { key: 'reason', label: 'Why', kind: 'textarea', hint: 'Required to cancel' },
        ],
        params: (v, r, key) => ({ p_trip_id: r?.id, p_status: asText(v.status), p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Updated.'),
        invalidates: ['trips', 'deliveries'],
      },
      {
        key: 'bring-back', label: 'Bring stock back', icon: Undo2, permission: 'delivery.record',
        rpc: 'return_undelivered', scope: 'row',
        available: (r) => r.status === 'DEPARTED' || r.status === 'RETURNED',
        intro: <>Whatever the round did not hand over, returned to the store by lot.</>,
        fields: [
          { key: 'product_variant_id', label: 'Variant', kind: 'ref', required: true, view: 'product_variants', labelColumns: ['code', 'name'] },
          { key: 'units', label: 'Units', kind: 'integer', required: true },
          { key: 'to_location_id', label: 'Back into', kind: 'ref', required: true, view: 'locations', labelColumns: ['code', 'name'] },
          { key: 'reason', label: 'Note', kind: 'text' },
        ],
        params: (v, r, key) => ({ p_trip_id: r?.id, p_product_variant_id: asText(v.product_variant_id), p_units: asInteger(v.units), p_to_location_id: asText(v.to_location_id), p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Back in store.'),
        invalidates: ['trips', 'stock'],
      },
    ],
    detail: { entityType: 'delivery.trips', title: (r) => `Round · ${String(r.code)}`, evidence: false },
  },

  // ── Customer stops ─────────────────────────────────────────────────────────────────────────────
  'trip-stops': {
    id: 'trip-stops', title: 'Customer Stops', captures: true,
    subtitle:
      'Every door on every round, in the order it was planned and the order it was actually driven. The basis column says how a door’s position was decided — by its GPS pin, or by the legacy round hint because it has none.',
    footer: 'The address, landmark and access notes are shown only to a reader who may see personal data',
    viewPermission: 'logistics.trips.view', view: 'trip_stops',
    select: 'id,trip_id,trip_code,service_date,sequence,sequence_basis,status,customer_code,customer_name,door_code,door_label,area,door_has_pin,route_order_hint,address_line,landmark,access_notes,arrived_at,completed_at,arrival_position_recorded,delivery_count,scans,bypasses,created_at,updated_at,row_version',
    searchColumns: ['customer_code', 'customer_name', 'door_code', 'trip_code'], defaultSort: 'service_date', defaultSortDirection: 'desc',
    filters: [
      { key: 'service_date', kind: 'dateRange', label: 'Service date' },
      { key: 'status', kind: 'select', label: 'Status', options: [
        { value: 'PENDING', label: 'Pending' }, { value: 'ARRIVED', label: 'At the door' },
        { value: 'COMPLETED', label: 'Done' }, { value: 'SKIPPED', label: 'Skipped' }] },
      { key: 'sequence_basis', kind: 'select', label: 'Ordered by', options: [
        { value: 'GPS', label: 'GPS pin' }, { value: 'HINT', label: 'No pin (legacy hint)' },
        { value: 'SOURCE', label: 'From the field' }, { value: 'MANUAL', label: 'By hand' }] },
      { key: 'trip_code', kind: 'search', label: 'Round', placeholder: 'Round code' },
    ],
    columns: [
      { key: 'sequence', header: 'Planned', width: '6rem', numeric: true },
      { key: 'sequence_basis', header: 'Ordered by', width: '8rem', render: STATUS_CELL('sequence_basis') },
      { key: 'customer_code', header: 'Customer', width: '11rem', mono: true },
      { key: 'customer_name', header: 'Name', width: '14rem' },
      { key: 'area', header: 'Area', width: '10rem' },
      { key: 'status', header: 'Status', width: '9rem', render: STATUS_CELL('status') },
      { key: 'delivery_count', header: 'Lines', width: '5rem', numeric: true },
      { key: 'scans', header: 'Scans', width: '5rem', numeric: true, optional: true },
      { key: 'bypasses', header: 'Bypassed', width: '7rem', numeric: true,
        render: (r) => (Number(r.bypasses) > 0
          ? <StatusBadge tone="refused" icon={QrCode} title="The door label was not scanned; an authorised person recorded why">{String(r.bypasses)}</StatusBadge>
          : null) },
      { key: 'trip_code', header: 'Round', width: '10rem', mono: true, optional: true },
      { key: 'arrived_at', header: 'Arrived', width: '11rem', optional: true, render: (r) => when(r.arrived_at) },
      { key: 'door_code', header: 'Door', width: '10rem', mono: true, optional: true },
      { key: 'landmark', header: 'Landmark', optional: true },
    ],
    actions: [
      {
        key: 'arrive', label: 'At the door', icon: MapPin, permission: 'delivery.record',
        rpc: 'record_stop_arrival', scope: 'row',
        available: (r) => r.status === 'PENDING' || r.status === 'ARRIVED',
        intro: <>Records arrival, and stamps the order the round is actually being driven in. A position is saved only if the device reports one; none is invented.</>,
        fields: [],
        params: (_v, r, key) => ({ p_stop_id: r?.id, p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Arrival recorded.'),
        invalidates: ['trip-stops'],
      },
      {
        key: 'scan-door', label: 'Scan the door label', icon: QrCode, permission: 'delivery.record',
        rpc: 'scan_door', scope: 'row',
        intro: <>The existing door label. A label that does not match this door is <strong>recorded as a mismatch</strong>, not refused — a rider at the wrong gate is the fact somebody needs.</>,
        fields: [{ key: 'payload', label: 'Label', kind: 'scan', required: true }],
        params: (v, r, key) => ({ p_stop_id: r?.id, p_payload: asScan(v.payload), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Scanned.'),
        invalidates: ['trip-stops'],
      },
      {
        key: 'bypass', label: 'Skip the label', icon: Ban, permission: 'delivery.qr.bypass',
        rpc: 'bypass_door', scope: 'row',
        intro: <>An audited decision, not a silent one. It needs a reason, and a door skipped repeatedly needs its label replaced.</>,
        fields: [
          { key: 'reason_code', label: 'Why', kind: 'select', required: true, options: [
            { value: 'QR_DAMAGED', label: 'The label would not scan' },
            { value: 'QR_MISSING', label: 'The door has no label yet' },
            { value: 'DEVICE_CAMERA', label: 'The device could not scan' }] },
          { key: 'note', label: 'Note', kind: 'textarea' },
        ],
        params: (v, r, key) => ({ p_stop_id: r?.id, p_reason_code: asText(v.reason_code), p_reason_note: asText(v.note), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Recorded.'),
        invalidates: ['trip-stops'],
      },
      {
        key: 'return-empties', label: 'Collect empties', icon: Undo2, permission: 'delivery.record',
        rpc: 'record_container_return', scope: 'row',
        intro: <>A counted return does not pretend to know which bottles came back; a scanned one names each. Neither carries a deposit — that decision is open.</>,
        fields: [
          { key: 'quantity', label: 'Containers', kind: 'integer', required: true },
          { key: 'condition', label: 'Condition', kind: 'select', options: [
            { value: 'GOOD', label: 'Good' }, { value: 'DAMAGED', label: 'Damaged' }] },
          { key: 'note', label: 'Note', kind: 'text' },
        ],
        params: (v, r, key) => ({ p_stop_id: r?.id, p_quantity: asInteger(v.quantity), p_condition: asText(v.condition) ?? 'GOOD', p_note: asText(v.note), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Recorded.'),
        invalidates: ['trip-stops', 'container-returns', 'bottle-custody'],
      },
      // One door onto another round. The client said this happens about monthly — rare enough that nobody
      // will build a habit around it, common enough that the alternative is somebody editing the database.
      {
        key: 'reassign', label: 'Move this door to another round', icon: Shuffle, permission: 'delivery.reassign',
        rpc: 'reassign_stop', scope: 'row',
        available: (r) => r.status === 'PENDING',
        intro: <>Only a door nobody has arrived at can move. Where the door has been reached, or anything was
          handed over, the command refuses and says so — the fix there is a correction, not a move.</>,
        fields: [
          { key: 'to_trip_id', label: 'To which round', kind: 'ref', required: true, view: 'trips', labelColumns: ['code', 'service_date'] },
          { key: 'reason', label: 'Why', kind: 'textarea', required: true },
        ],
        params: (v, r, key) => ({ p_stop_id: r?.id, p_to_trip_id: asText(v.to_trip_id), p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Moved.'),
        invalidates: ['trip-stops', 'trips', 'deliveries'],
      },
      // The two bulk actions PREVIEW by default. `confirm` off returns what would happen and changes nothing,
      // which is the only honest way to offer an action over rows somebody has not read one by one.
      {
        key: 'bulk-move', label: 'Move several doors', icon: Shuffle, permission: 'delivery.bulk',
        rpc: 'bulk_move_stops', scope: 'global',
        intro: <>Paste the stop ids, one per line. Leave <strong>apply</strong> off first: it answers with
          what would move and what would not, and changes nothing.</>,
        fields: [
          { key: 'stop_ids', label: 'Stops', kind: 'textarea', required: true, hint: 'One id per line' },
          { key: 'to_trip_id', label: 'To which round', kind: 'ref', required: true, view: 'trips', labelColumns: ['code', 'service_date'] },
          { key: 'reason', label: 'Why', kind: 'textarea', required: true },
          { key: 'confirm', label: 'Apply', kind: 'boolean', hint: 'Off shows what would happen' },
        ],
        params: (v, _r, key) => ({
          p_stop_ids: asIdList(v.stop_ids), p_to_trip_id: asText(v.to_trip_id),
          p_reason: asText(v.reason), p_confirm: v.confirm === true, p_idempotency_key: key,
        }),
        success: (r) => String(r.detail ?? 'Done.'),
        invalidates: ['trip-stops', 'trips', 'deliveries'],
      },
      {
        key: 'bulk-status', label: 'Set an outcome on several deliveries', icon: ListChecks, permission: 'delivery.bulk',
        rpc: 'bulk_set_delivery_status', scope: 'global',
        intro: <>For the round that did not go out at all — a breakdown, a strike, a flood. Every row gets the
          same reason, which is what makes it honest: one cause, recorded once, on each delivery it explains.</>,
        fields: [
          { key: 'delivery_ids', label: 'Deliveries', kind: 'textarea', required: true, hint: 'One id per line' },
          { key: 'status', label: 'Outcome', kind: 'select', required: true, options: [
            { value: 'FAILED', label: 'Not delivered' }, { value: 'SKIPPED', label: 'Skipped' },
            { value: 'RESCHEDULED', label: 'Moved to another day' }] },
          { key: 'reason_code', label: 'Reason', kind: 'select', required: true, options: [
            { value: 'VEHICLE_BREAKDOWN', label: 'The vehicle did not make it' },
            { value: 'RIDER_UNAVAILABLE', label: 'The rider could not finish the round' },
            { value: 'OUT_OF_ROUND', label: 'The door was not reached' },
            { value: 'SHORT_LOADED', label: 'Less was loaded than the round needed' }] },
          { key: 'note', label: 'Note', kind: 'textarea' },
          { key: 'confirm', label: 'Apply', kind: 'boolean', hint: 'Off shows what would happen' },
        ],
        params: (v, _r, key) => ({
          p_delivery_ids: asIdList(v.delivery_ids), p_status: asText(v.status),
          p_reason_code: asText(v.reason_code), p_note: asText(v.note),
          p_confirm: v.confirm === true, p_idempotency_key: key,
        }),
        success: (r) => String(r.detail ?? 'Done.'),
        invalidates: ['deliveries', 'trip-stops', 'trips'],
      },
    ],
    detail: { entityType: 'delivery.trip_stops', title: (r) => `${String(r.customer_name)} · ${String(r.door_code)}`, evidence: false },
  },

  // ── Deliveries ─────────────────────────────────────────────────────────────────────────────────
  deliveries: {
    id: 'deliveries', title: 'Delivery', captures: true,
    subtitle:
      'What was actually handed over, per variant, per door. A blank delivered column means nobody recorded it — which is different from delivering nothing, and the ERP will not decide which for them.',
    footer: 'Nothing here carries money. What a delivery is worth comes with billing, and the prices are still the owner’s to set',
    viewPermission: 'delivery.view', view: 'deliveries',
    select: 'id,code,service_date,status,ordered_units,delivered_units,variant_code,variant_name,container_volume_ml,fill_volume_ml,sequence,sequence_basis,stop_status,trip_id,trip_code,route_code,rider_name,customer_code,customer_name,door_code,door_label,area,door_has_pin,source_order_id,reason_code,reason_note,source_app,device_label,captured_at,proof_count,trip_stop_id,moved_from_the_door,door_trip_code,door_rider_name,assignment_reason,created_at,updated_at,row_version',
    searchColumns: ['code', 'customer_code', 'customer_name', 'trip_code'], defaultSort: 'service_date', defaultSortDirection: 'desc',
    filters: [
      { key: 'service_date', kind: 'dateRange', label: 'Service date' },
      { key: 'status', kind: 'select', label: 'Outcome', options: [
        { value: 'PENDING', label: 'Not recorded' }, { value: 'IN_PROGRESS', label: 'In progress' },
        { value: 'DELIVERED', label: 'Delivered' }, { value: 'PARTIAL', label: 'Part delivered' },
        { value: 'FAILED', label: 'Not delivered' }, { value: 'SKIPPED', label: 'Skipped' },
        { value: 'RESCHEDULED', label: 'Moved to another day' }] },
      { key: 'route_code', kind: 'search', label: 'Route', placeholder: 'Route code' },
      { key: 'variant_code', kind: 'search', label: 'Variant', placeholder: 'Variant code' },
    ],
    columns: [
      { key: 'code', header: 'Delivery', width: '10rem', mono: true },
      { key: 'service_date', header: 'Date', width: '9rem' },
      { key: 'status', header: 'Outcome', width: '10rem', render: STATUS_CELL('status') },
      { key: 'customer_name', header: 'Customer', width: '14rem' },
      { key: 'variant_code', header: 'Variant', width: '7rem', mono: true },
      { key: 'delivered_units', header: 'Handed over', width: '9rem', numeric: true, render: handedOver },
      { key: 'reason_code', header: 'Reason', width: '12rem', render: (r) => (r.reason_code ? String(r.reason_code).replace(/_/g, ' ').toLowerCase() : null) },
      { key: 'proof_count', header: 'Proof', width: '5rem', numeric: true, render: (r) => (Number(r.proof_count) > 0 ? String(r.proof_count) : <span className="text-muted">—</span>) },
      { key: 'rider_name', header: 'Rider', width: '12rem', optional: true },
      { key: 'door_rider_name', header: 'Door’s rider', width: '12rem', optional: true,
        // Only interesting when it differs: otherwise it restates the column above it.
        render: (r) => (r.moved_from_the_door
          ? <span title={String(r.assignment_reason ?? '')}>{String(r.door_rider_name ?? '—')}</span>
          : <span className="text-muted">same</span>) },
      { key: 'route_code', header: 'Route', width: '7rem', mono: true, optional: true },
      { key: 'area', header: 'Area', width: '10rem', optional: true },
      { key: 'source_app', header: 'Recorded in', width: '9rem', optional: true },
      { key: 'captured_at', header: 'Recorded', width: '11rem', optional: true, render: (r) => when(r.captured_at) },
    ],
    actions: [
      {
        key: 'record', label: 'Record what was handed over', icon: CheckCircle2, permission: 'delivery.record',
        rpc: 'record_delivery', scope: 'row',
        available: (r) => r.status === 'PENDING' || r.status === 'IN_PROGRESS',
        intro: <>Records the outcome and, for units that actually changed hands, the stock movement. No charge, no deduction and no fault is attached to any outcome.</>,
        fields: [
          { key: 'status', label: 'Outcome', kind: 'select', required: true, options: [
            { value: 'DELIVERED', label: 'Delivered in full' }, { value: 'PARTIAL', label: 'Part delivered' },
            { value: 'FAILED', label: 'Not delivered' }, { value: 'SKIPPED', label: 'Skipped' },
            { value: 'RESCHEDULED', label: 'Moved to another day' }] },
          { key: 'delivered_units', label: 'Units handed over', kind: 'integer', hint: 'Required for a part delivery' },
          { key: 'reason_code', label: 'Reason', kind: 'select', hint: 'Required for anything but a full delivery', options: [
            { value: 'CUSTOMER_ABSENT', label: 'Nobody at the door' },
            { value: 'CUSTOMER_REFUSED', label: 'The customer refused it' },
            { value: 'ADDRESS_NOT_FOUND', label: 'The door could not be found' },
            { value: 'GATE_LOCKED', label: 'No access to the door' },
            { value: 'VEHICLE_BREAKDOWN', label: 'The vehicle did not make it' },
            { value: 'RIDER_UNAVAILABLE', label: 'The rider could not finish the round' },
            { value: 'SHORT_LOADED', label: 'Less was loaded than the round needed' },
            { value: 'CUSTOMER_TOOK_LESS', label: 'The customer wanted fewer today' },
            { value: 'PRODUCT_DAMAGED', label: 'Some was not fit to hand over' },
            { value: 'CUSTOMER_PAUSED', label: 'The customer had paused' },
            { value: 'OUT_OF_ROUND', label: 'The door was not reached' }] },
          { key: 'rescheduled_to', label: 'Moved to', kind: 'date', hint: 'Required when moving it to another day' },
          { key: 'note', label: 'Note', kind: 'textarea' },
        ],
        params: (v, r, key) => ({
          p_delivery_id: r?.id, p_status: asText(v.status), p_delivered_units: asInteger(v.delivered_units),
          p_reason_code: asText(v.reason_code), p_note: asText(v.note),
          p_rescheduled_to: asText(v.rescheduled_to), p_idempotency_key: key,
        }),
        success: (r) => String(r.detail ?? 'Recorded.'),
        invalidates: ['deliveries', 'trips', 'trip-stops', 'stock'],
      },
      {
        key: 'scan-product', label: 'Scan a container out', icon: QrCode, permission: 'delivery.record',
        rpc: 'scan_filled_product', scope: 'row',
        intro: <>Moves that container’s custody to the customer. Optional: scanning every bottle at a doorstep is a real cost, and a delivery without scans is still a delivery.</>,
        fields: [{ key: 'payload', label: 'Container label', kind: 'scan', required: true }],
        params: (v, r, key) => ({ p_delivery_id: r?.id, p_payload: asScan(v.payload), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Scanned.'),
        invalidates: ['deliveries', 'bottle-custody'],
      },
      {
        key: 'capture-pod', label: 'Capture proof', icon: CheckCircle2, permission: 'delivery.record',
        rpc: 'capture_pod', scope: 'row',
        intro: <>Evidence, not a precondition. A delivery without proof is still a delivery.</>,
        fields: [
          { key: 'kind', label: 'Kind', kind: 'select', required: true, options: [
            { value: 'ACKNOWLEDGEMENT', label: 'The customer acknowledged it' },
            { value: 'OTP', label: 'One-time code' },
            { value: 'QR_SCAN', label: 'Door label scan' },
            { value: 'NOTE', label: 'A note' }] },
          { key: 'acknowledged_by', label: 'Who acknowledged it', kind: 'text' },
          { key: 'note', label: 'Note', kind: 'textarea' },
        ],
        params: (v, r, key) => ({ p_stop_id: r?.trip_stop_id, p_kind: asText(v.kind), p_delivery_id: r?.id, p_acknowledged_by: asText(v.acknowledged_by), p_note: asText(v.note), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Captured.'),
        invalidates: ['deliveries', 'proof-of-delivery'],
      },
      // Correcting is not recording. It overrules a first-hand account, so it is a separate command with a
      // separate permission, a required reason from its own taxonomy, and a row of its own in the register
      // of corrections — where what it was is kept beside what it became.
      {
        key: 'correct', label: 'Correct this outcome', icon: PencilLine, permission: 'delivery.correct',
        rpc: 'correct_delivery', scope: 'row',
        available: (r) => r.status !== 'PENDING',
        intro: <>What the rider recorded stays readable. This does <strong>not</strong> adjust the stock
          ledger: what physically left the store is a separate fact from what somebody wrote down, and
          inventing a movement to balance a typo would corrupt both.</>,
        fields: [
          { key: 'status', label: 'It should say', kind: 'select', required: true, options: [
            { value: 'DELIVERED', label: 'Delivered in full' }, { value: 'PARTIAL', label: 'Part delivered' },
            { value: 'FAILED', label: 'Not delivered' }, { value: 'SKIPPED', label: 'Skipped' }] },
          { key: 'delivered_units', label: 'Units handed over', kind: 'integer', hint: 'Required for a part delivery' },
          { key: 'reason_code', label: 'Why it was wrong', kind: 'select', required: true, options: [
            { value: 'RIDER_ENTERED_WRONG', label: 'The rider recorded the wrong outcome' },
            { value: 'RECORDED_LATE', label: 'It happened but was never entered' },
            { value: 'CUSTOMER_DISPUTED', label: 'The customer says otherwise and is right' },
            { value: 'WRONG_DELIVERY_ROW', label: 'It was entered against the wrong door' },
            { value: 'SYNC_ERROR', label: 'A sync problem produced the wrong outcome' }] },
          { key: 'note', label: 'What happened', kind: 'textarea', required: true },
        ],
        params: (v, r, key) => ({
          p_delivery_id: r?.id, p_status: asText(v.status), p_reason_code: asText(v.reason_code),
          p_note: asText(v.note), p_delivered_units: asInteger(v.delivered_units),
          p_expected_version: r?.row_version, p_idempotency_key: key,
        }),
        success: (r) => String(r.detail ?? 'Corrected.'),
        invalidates: ['deliveries', 'delivery-corrections', 'trip-stops'],
      },
      // Moving ONE variant, where moving the door would move the rest of it too.
      {
        key: 'reassign-delivery', label: 'Move this one to another round', icon: Shuffle, permission: 'delivery.reassign',
        rpc: 'reassign_delivery', scope: 'row',
        available: (r) => r.status === 'PENDING' || r.status === 'IN_PROGRESS',
        intro: <>The rest of the door stays where it is. Only a round on the <strong>same day</strong> can
          take it — moving a delivery to another day is a reschedule, which is a different fact.</>,
        fields: [
          { key: 'to_trip_id', label: 'Carried by', kind: 'ref', required: true, view: 'trips', labelColumns: ['code', 'service_date'] },
          { key: 'reason', label: 'Why', kind: 'textarea', required: true },
        ],
        params: (v, r, key) => ({ p_delivery_id: r?.id, p_to_trip_id: asText(v.to_trip_id), p_reason: asText(v.reason), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Moved.'),
        invalidates: ['deliveries', 'delivery-assignments', 'trips', 'trip-stops'],
      },
    ],
    detail: { entityType: 'delivery.deliveries', title: (r) => `Delivery · ${String(r.code)}`, evidence: false },
  },

  // ── Proof of delivery ──────────────────────────────────────────────────────────────────────────
  'proof-of-delivery': {
    id: 'proof-of-delivery', title: 'Proof of Delivery', captures: false,
    subtitle:
      'The evidence captured at a door. It sits behind its own permission: seeing that a delivery happened and seeing a photograph of somebody’s front door are different things to be allowed.',
    footer: 'Proof is never required. On the upstream field data it exists on fewer than one delivery in five hundred',
    viewPermission: 'delivery.pod.view', view: 'proof_of_delivery',
    select: 'id,kind,captured_at,attachment_id,otp_verified,acknowledged_by,position_recorded,note,source_app,delivery_id,delivery_code,customer_code,service_date,trip_code,created_at,row_version',
    searchColumns: ['delivery_code', 'customer_code', 'trip_code'], defaultSort: 'captured_at', defaultSortDirection: 'desc',
    filters: [
      { key: 'service_date', kind: 'dateRange', label: 'Service date' },
      { key: 'kind', kind: 'select', label: 'Kind', options: [
        { value: 'PHOTO', label: 'Photograph' }, { value: 'SIGNATURE', label: 'Signature' },
        { value: 'OTP', label: 'One-time code' }, { value: 'QR_SCAN', label: 'Door label' },
        { value: 'ACKNOWLEDGEMENT', label: 'Acknowledged' }, { value: 'GPS', label: 'Position' },
        { value: 'NOTE', label: 'Note' }] },
    ],
    columns: [
      { key: 'captured_at', header: 'Captured', width: '11rem', render: (r) => when(r.captured_at) },
      { key: 'kind', header: 'Kind', width: '10rem' },
      { key: 'delivery_code', header: 'Delivery', width: '10rem', mono: true },
      { key: 'customer_code', header: 'Customer', width: '11rem', mono: true },
      { key: 'service_date', header: 'Date', width: '9rem' },
      { key: 'acknowledged_by', header: 'Acknowledged by', width: '12rem' },
      { key: 'position_recorded', header: 'Position', width: '7rem', render: (r) => (r.position_recorded ? 'Recorded' : <span className="text-muted">None</span>) },
      { key: 'source_app', header: 'Captured in', width: '9rem', optional: true },
      { key: 'note', header: 'Note', optional: true },
    ],
    actions: [],
    detail: { entityType: 'delivery.proof_of_delivery', title: (r) => `Proof · ${String(r.kind)}`, evidence: true },
  },

  // ── Returns desk ───────────────────────────────────────────────────────────────────────────────
  'container-returns': {
    id: 'container-returns', title: 'Returns Desk', captures: true,
    subtitle:
      'Empty containers collected at a door. A return is its own event: a customer hands back bottles from any day, so it is never tied one-to-one to today’s delivery.',
    footer: 'No deposit, no refund, no charge. Whether a container carries a deposit is an unanswered client decision',
    viewPermission: 'delivery.view', view: 'container_returns',
    select: 'id,captured_at,quantity,basis,condition,reason_code,reason_note,customer_code,customer_name,bottle_type_code,trip_code,service_date,source_app,bottles_identified,created_at,row_version',
    searchColumns: ['customer_code', 'customer_name', 'trip_code'], defaultSort: 'captured_at', defaultSortDirection: 'desc',
    filters: [
      { key: 'service_date', kind: 'dateRange', label: 'Service date' },
      { key: 'basis', kind: 'select', label: 'Counted or scanned', options: [
        { value: 'SCANNED', label: 'Scanned' }, { value: 'COUNTED', label: 'Counted' }] },
      { key: 'condition', kind: 'select', label: 'Condition', options: [
        { value: 'GOOD', label: 'Good' }, { value: 'DAMAGED', label: 'Damaged' }] },
    ],
    columns: [
      { key: 'captured_at', header: 'Collected', width: '11rem', render: (r) => when(r.captured_at) },
      { key: 'customer_name', header: 'Customer', width: '14rem' },
      { key: 'quantity', header: 'Containers', width: '7rem', numeric: true },
      { key: 'basis', header: 'How counted', width: '9rem',
        render: (r) => (r.basis === 'SCANNED'
          ? <StatusBadge tone="accepted" icon={QrCode}>Scanned</StatusBadge>
          : <StatusBadge tone="neutral" icon={AlertTriangle} title="Counted, so which specific containers came back is not known and has not been guessed">Counted</StatusBadge>) },
      { key: 'bottles_identified', header: 'Identified', width: '7rem', numeric: true },
      { key: 'condition', header: 'Condition', width: '8rem' },
      { key: 'bottle_type_code', header: 'Type', width: '9rem', mono: true, optional: true },
      { key: 'trip_code', header: 'Round', width: '10rem', mono: true, optional: true },
      { key: 'reason_note', header: 'Note', optional: true },
    ],
    actions: [],
    detail: { entityType: 'delivery.container_returns', title: (r) => `Return · ${String(r.customer_name)}`, evidence: false },
  },

  // ── Exceptions ─────────────────────────────────────────────────────────────────────────────────
  'delivery-exceptions': {
    id: 'delivery-exceptions', title: 'Delivery Exceptions', captures: false,
    subtitle:
      'Something went wrong on a round. Each record is the operational fact and nothing else: no fault is attributed, no pay is affected and no charge is raised.',
    footer: 'The responsibility and penalty rules are a client proposal nobody has confirmed, so nothing here has a financial consequence',
    viewPermission: 'delivery.view', view: 'delivery_exceptions',
    select: 'id,kind,status,severity,detail,reason_code,raised_at,resolved_at,resolution_note,trip_code,service_date,source_app,trip_id,trip_stop_id,delivery_id,created_at,updated_at,row_version',
    searchColumns: ['trip_code', 'detail'], defaultSort: 'raised_at', defaultSortDirection: 'desc',
    filters: [
      { key: 'service_date', kind: 'dateRange', label: 'Service date' },
      { key: 'status', kind: 'select', label: 'Status', defaultValue: 'OPEN', options: [
        { value: 'OPEN', label: 'Open' }, { value: 'ACKNOWLEDGED', label: 'Acknowledged' }, { value: 'RESOLVED', label: 'Resolved' }] },
      { key: 'kind', kind: 'select', label: 'Kind', options: [
        { value: 'CUSTOMER_UNAVAILABLE', label: 'Customer unavailable' }, { value: 'PARTIAL_DELIVERY', label: 'Part delivered' },
        { value: 'DELIVERY_REJECTED', label: 'Delivery refused' }, { value: 'SHORTAGE', label: 'Shortage' },
        { value: 'PRODUCT_DAMAGED', label: 'Product damaged' }, { value: 'WRONG_LOCATION', label: 'Wrong door' },
        { value: 'WRONG_PRODUCT', label: 'Wrong product' }, { value: 'EMPTY_NOT_RETURNED', label: 'Empties not returned' },
        { value: 'VEHICLE_ISSUE', label: 'Vehicle' }, { value: 'RIDER_ISSUE', label: 'Rider' },
        { value: 'QR_UNAVAILABLE', label: 'Door label' }, { value: 'SYNC_FAILURE', label: 'Sync' }] },
    ],
    columns: [
      { key: 'raised_at', header: 'Raised', width: '11rem', render: (r) => when(r.raised_at) },
      { key: 'kind', header: 'Kind', width: '14rem', render: (r) => String(r.kind).replace(/_/g, ' ').toLowerCase() },
      { key: 'status', header: 'Status', width: '9rem', render: STATUS_CELL('status') },
      { key: 'severity', header: 'Severity', width: '7rem' },
      { key: 'trip_code', header: 'Round', width: '10rem', mono: true },
      { key: 'service_date', header: 'Date', width: '9rem' },
      { key: 'detail', header: 'Detail' },
      { key: 'resolution_note', header: 'How it was resolved', optional: true },
    ],
    actions: [
      {
        key: 'raise', label: 'Raise an exception', icon: AlertTriangle, permission: 'delivery.exception.manage',
        rpc: 'raise_delivery_exception', scope: 'global',
        intro: <>Records the operational fact. No fault, no charge and no deduction is attached — those rules are unconfirmed.</>,
        fields: [
          { key: 'kind', label: 'Kind', kind: 'select', required: true, options: [
            { value: 'CUSTOMER_UNAVAILABLE', label: 'Customer unavailable' }, { value: 'SHORTAGE', label: 'Shortage' },
            { value: 'PRODUCT_DAMAGED', label: 'Product damaged' }, { value: 'WRONG_LOCATION', label: 'Wrong door' },
            { value: 'WRONG_PRODUCT', label: 'Wrong product' }, { value: 'EMPTY_NOT_RETURNED', label: 'Empties not returned' },
            { value: 'VEHICLE_ISSUE', label: 'Vehicle' }, { value: 'RIDER_ISSUE', label: 'Rider' },
            { value: 'QR_UNAVAILABLE', label: 'Door label' }, { value: 'SYNC_FAILURE', label: 'Sync' }] },
          { key: 'trip_id', label: 'Round', kind: 'ref', view: 'trips', labelColumns: ['code', 'service_date'] },
          { key: 'severity', label: 'Severity', kind: 'select', options: [
            { value: 'INFO', label: 'Information' }, { value: 'MEDIUM', label: 'Medium' }, { value: 'HIGH', label: 'High' }] },
          { key: 'detail', label: 'What happened', kind: 'textarea', required: true },
        ],
        params: (v, _r, key) => ({ p_kind: asText(v.kind), p_trip_id: asText(v.trip_id), p_severity: asText(v.severity) ?? 'MEDIUM', p_detail: asText(v.detail), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Raised.'),
        invalidates: ['delivery-exceptions', 'trips'],
      },
      {
        key: 'resolve', label: 'Resolve', icon: CheckCircle2, permission: 'delivery.exception.manage',
        rpc: 'resolve_delivery_exception', scope: 'row',
        available: (r) => r.status !== 'RESOLVED',
        intro: <>Still no financial consequence attached.</>,
        fields: [{ key: 'note', label: 'How it was resolved', kind: 'textarea', required: true }],
        params: (v, r, key) => ({ p_exception_id: r?.id, p_resolution_note: asText(v.note), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Resolved.'),
        invalidates: ['delivery-exceptions'],
      },
    ],
    detail: { entityType: 'delivery.delivery_exceptions', title: (r) => `Exception · ${String(r.kind)}`, evidence: false },
  },

  // ── Bottle custody ─────────────────────────────────────────────────────────────────────────────
  'bottle-custody': {
    id: 'bottle-custody', title: 'Bottle Custody', captures: false,
    subtitle:
      'Where every container is, from an append-only event log rather than a running count. A bottle with no events has never been scanned anywhere — which is most of them, and is worth seeing.',
    footer: 'A customer who leaves with bottles is billed for each one not back after 7 days: ₹50 for 1 L, ₹40 for 500 ml (Settings › finance.lost_bottle_charge). A broken glass keeps its label: “Active again on new glass”.',
    viewPermission: 'bottles.view', view: 'bottle_custody',
    select: 'id,code,custody_state,held_since,last_custody_event_at,bottle_type_code,bottle_type_name,customer_code,customer_name,event_count,last_event_at,status,created_at,row_version',
    searchColumns: ['code', 'customer_code', 'customer_name'], defaultSort: 'code',
    filters: [
      { key: 'custody_state', kind: 'select', label: 'Where it is', options: [
        { value: 'WITH_FLEET', label: 'At plant' }, { value: 'IN_TRANSIT', label: 'On the road' },
        { value: 'WITH_CUSTOMER', label: 'With a customer' }, { value: 'LOST', label: 'Lost' },
        { value: 'RETIRED', label: 'Retired' }] },
      { key: 'bottle_type_code', kind: 'search', label: 'Type', placeholder: 'Type code' },
    ],
    columns: [
      { key: 'code', header: 'Container', width: '12rem', mono: true },
      { key: 'custody_state', header: 'Where', width: '11rem', render: STATUS_CELL('custody_state') },
      { key: 'customer_name', header: 'Held by', width: '14rem', render: (r) => (r.customer_name ? String(r.customer_name) : <span className="text-muted">—</span>) },
      { key: 'held_since', header: 'Since', width: '11rem', render: (r) => when(r.held_since) },
      { key: 'bottle_type_code', header: 'Type', width: '10rem', mono: true },
      { key: 'event_count', header: 'Events', width: '6rem', numeric: true,
        render: (r) => (Number(r.event_count) > 0
          ? String(r.event_count)
          : <span className="text-muted" title="This container has never been scanned anywhere">never scanned</span>) },
      { key: 'last_event_at', header: 'Last seen', width: '11rem', optional: true, render: (r) => when(r.last_event_at) },
      { key: 'status', header: 'In service', width: '8rem', optional: true },
    ],
    actions: [
      // The correction is itself a custody event, appended like any other. No historical event is edited,
      // so the trail stays the whole story — including the mistake and the day somebody noticed it.
      {
        key: 'correct-custody', label: 'Correct where it is', icon: PencilLine, permission: 'bottles.custody.correct',
        rpc: 'correct_custody', scope: 'row',
        intro: <>For a mis-scan, a scan nobody made, or one found at the dairy that the system believes a
          customer is holding.</>,
        fields: [
          { key: 'state', label: 'It is actually', kind: 'select', required: true, options: [
            { value: 'WITH_FLEET', label: 'At plant' }, { value: 'IN_TRANSIT', label: 'On the road' },
            { value: 'WITH_CUSTOMER', label: 'With a customer' }, { value: 'LOST', label: 'Lost' }] },
          { key: 'customer_id', label: 'Which customer', kind: 'ref', view: 'customers', labelColumns: ['code', 'name'], hint: 'Only when it is with a customer' },
          { key: 'reason_code', label: 'Why the record was wrong', kind: 'select', required: true, options: [
            { value: 'MIS_SCAN', label: 'The wrong container was scanned' },
            { value: 'MISSED_SCAN', label: 'It changed hands and was never scanned' },
            { value: 'FOUND_AT_DAIRY', label: 'Recorded as with a customer; it is here' },
            { value: 'CONFIRMED_LOST', label: 'It is not coming back' }] },
          { key: 'note', label: 'What happened', kind: 'textarea', required: true },
        ],
        params: (v, r, key) => ({
          p_bottle_id: r?.id, p_state: asText(v.state), p_reason_code: asText(v.reason_code),
          p_note: asText(v.note), p_customer_id: asText(v.customer_id), p_idempotency_key: key,
        }),
        success: (r) => String(r.detail ?? 'Corrected.'),
        invalidates: ['bottle-custody', 'custody-corrections'],
      },
      // The owner, 2026-10-12: a relabel keeps the bottle's QR — the label goes on new glass and the bottle is active again.
      {
        key: 'new-glass', label: 'Active again on new glass', icon: PencilLine, permission: 'bottles.custody.correct',
        rpc: 'bottle_on_new_glass', scope: 'row',
        intro: <>The same label and QR go on a new glass. The bottle is in service again, at the plant and empty; its history keeps the old glass.</>,
        fields: [{ key: 'note', label: 'Why (the new bottle note)', kind: 'textarea', required: true }],
        params: (v, r, key) => ({ p_bottle_id: r?.id, p_note: asText(v.note), p_idempotency_key: key }),
        success: (r) => String(r.detail ?? 'Active again on new glass.'),
        invalidates: ['bottle-custody', 'custody-corrections'],
      },
    ],
    detail: { entityType: 'assets.bottles', title: (r) => `Container · ${String(r.code)}`, evidence: false },
  },
};
